import { clamp, mean, std } from './math';
import { centsOffNearestSemitone, stabilityScore } from './metrics';
import { freqToMidi } from './note';

import type {
  BreathSegment,
  BreathSegmentId,
  PitchRegister,
  PitchRegisterId,
  PitchSample,
  RangeInfo,
} from './types';

/**
 * segments — the take cut into stretches, so the report can say *when* the voice
 * wavered (breath) and *where in the range* it wavered (pitch registers) instead of
 * only how much it wavered overall.
 *
 * A single steadiness number cannot tell a singer who starts shaky and settles from
 * one who starts well and runs out of air; those two want opposite advice. The whole
 * point of the section is the shape over time.
 *
 * Framework-free and pure — vitest target.
 */

/**
 * Where the breath section cuts the take, as **fractions of the take's own length**.
 *
 * The three stretches are the design's (`1000:8628` — 시작 / 중간 / 후반), and the
 * proportions are its ten-second example read as thirds-ish: 0-3 s, 3-7 s, 7-10 s.
 *
 * Fractions and not seconds, because the seconds were a lie about every take that
 * was not ten seconds long. A singer who held the note for six seconds got two
 * scores and a permanently unscored 후반 — the stretch existed only past the end of
 * their recording, so the screen said "not measured yet" about a stretch the take
 * could never reach. Cut by proportion and every take has a beginning, a middle and
 * an end, which is what the copy beside each score ("안정적으로 시작했어요") is
 * actually about.
 *
 * This array is the only place the proportions are written down. The seconds each
 * take resolves them to travel with the result (`BreathSegment.fromSec`) — a screen
 * that repeats `0~3초` in its own string is wrong for most takes.
 */
export const BREATH_SEGMENTS: readonly {
  id: BreathSegmentId;
  from: number;
  to: number;
}[] = [
  { id: 'start', from: 0, to: 0.3 },
  { id: 'middle', from: 0.3, to: 0.7 },
  { id: 'late', from: 0.7, to: 1 },
];

/**
 * Readings a stretch needs before it gets a score. Steadiness is a *spread*, and the
 * spread of a handful of readings is mostly luck — at the production frame rate 16
 * readings is about a third of a second of held tone. Under that the stretch is
 * absent rather than scored: "the take barely reached here" is not "the voice was
 * unsteady here".
 */
const MIN_READINGS = 16;

/**
 * Seconds are rounded to a tenth before anything uses them, so the boundary a row is
 * *labelled* with is the boundary its score was *measured* at. Formatting the raw
 * fraction at render time instead would let a row say `0~1.8초` about readings cut
 * at 1.7999999999999998.
 */
const roundSec = (sec: number): number => Math.round(sec * 10) / 10;

/**
 * Cut the take into its three stretches and score each one.
 *
 * All three always come back — a stretch with too few readings has no `score`, like
 * `PitchRegister`, so the screen can still draw its row and say when it was. Only a
 * take with no length at all (empty, or every reading on one timestamp) has nothing
 * to divide.
 */
export const breathSegments = (samples: PitchSample[]): BreathSegment[] => {
  if (samples.length === 0) {
    return [];
  }
  // Timestamps are device uptime, so the take's own first reading is second zero.
  const origin = samples[0].tMs;
  const spanSec = (samples[samples.length - 1].tMs - origin) / 1000;
  if (spanSec <= 0) {
    return [];
  }

  return BREATH_SEGMENTS.map(({ id, from, to }, i) => {
    const fromSec = roundSec(from * spanSec);
    const toSec = roundSec(to * spanSec);
    const last = i === BREATH_SEGMENTS.length - 1;
    const inside = samples.filter((s) => {
      const sec = (s.tMs - origin) / 1000;
      // The final stretch has no upper bound: `toSec` is rounded to a tenth, so a
      // take ending at 5.94 s is labelled 5.9 and a bound there would drop its
      // last readings.
      return sec >= fromSec && (last || sec < toSec);
    });
    if (inside.length < MIN_READINGS) {
      return { id, fromSec, toSec };
    }
    const cents = centsOffNearestSemitone(inside.map((s) => s.f0Hz));
    return { id, fromSec, toSec, score: Math.round(stabilityScore(cents)) };
  });
};

/**
 * The breath section's headline score: the mean of the stretches that were scored.
 *
 * It is built from the stretches and nothing else, so the number above the three
 * segment scores always reads as their summary. **Unscored stretches stay out of the
 * divisor** — a stretch the take barely reached is not a stretch that went badly, and
 * counting it as one would punish a short take twice. No scored stretch at all is
 * `null` ("not measured"), never 0.
 */
export const breathStabilityScore = (
  segments: readonly BreathSegment[],
): number | null => {
  const scored = segments.flatMap((s) =>
    s.score === undefined ? [] : [s.score],
  );
  return scored.length === 0 ? null : Math.round(mean(scored));
};

/**
 * Where the pitch section cuts the voice, as fractions of the measured range
 * (design §8.5). Fractions and not fixed notes: the design's own example splits a
 * G2~E5 voice at G3 / D#4 / G4 / D5, and those cuts are that singer's passaggio —
 * pinning them to MIDI numbers would put a soprano's whole range inside 저역 확장.
 *
 * Four of the five reproduce the design's example. **The top cut does not**: the
 * design put it at 0.96, which is 4% of a range — about one semitone on two octaves.
 * Filling `MIN_STEPS` inside one semitone means holding that note for about a third
 * of a second, and the high test is a glide, so the readings up there rarely settle
 * long enough to gather. The band was scorable in principle and nearly never scored
 * in practice, which put "not measured yet" under a band the singer had no way to
 * fill. 0.88 gives it three semitones on two octaves — wide enough that a glide
 * passing through leaves consecutive readings behind.
 *
 * The width comes out of `head`, which drops from 21% to 13%. That is the trade this
 * makes: on a range of about an octave `head` narrows to two semitones and can go
 * unscored the way 고역 확장 used to. The top band is the one worth protecting —
 * it is where the singer is being asked to stretch.
 *
 * They are boundaries of a proportion, so they say nothing about anyone's vocal
 * anatomy — a rough map, not a diagnosis, which is why the screen calls the axis
 * 안정성.
 */
export const PITCH_REGISTERS: readonly {
  id: PitchRegisterId;
  from: number;
  to: number;
}[] = [
  { id: 'lowExtension', from: 0, to: 0.39 },
  { id: 'modal', from: 0.39, to: 0.63 },
  { id: 'passaggio', from: 0.63, to: 0.75 },
  { id: 'head', from: 0.75, to: 0.88 },
  { id: 'highExtension', from: 0.88, to: 1 },
];

/**
 * Longest hole between two readings that still counts as one continuous phrase (ms).
 * Steadiness here is read from the step between consecutive readings, and a step
 * taken across a breath is not a step the voice made — joined, a singer who paused
 * and came back on a different note looks like a singer whose voice cracked.
 *
 * A non-positive gap is dropped for the same reason: callers hand this readings
 * gathered from several takes, and two readings that did not follow each other in
 * time have no step between them.
 */
const MAX_STEP_MS = 60;

/**
 * Steps a register needs before it gets a score, and the spread that scores zero.
 *
 * 16 steps is about a third of a second of singing. 50 cents of spread between
 * consecutive frames is half a semitone of movement *per frame* — at that point the
 * voice is not moving, it is jumping. A smooth slide has an almost constant step and
 * so scores high, which is right: sliding under control is steady.
 */
const MIN_STEPS = 16;
const ZERO_SPREAD_CENTS = 50;

/**
 * The voice's range cut into five registers, each scored on how controlled it was
 * there. `range` is null when the session never measured the low and high ends —
 * without it there is nothing to divide, so there is nothing to say.
 */
export const pitchRegisters = (
  samples: PitchSample[],
  range: RangeInfo | null,
  /** The fractions to cut at. Defaults to the design's five (see `PITCH_REGISTERS`). */
  cuts: typeof PITCH_REGISTERS = PITCH_REGISTERS,
): PitchRegister[] => {
  if (!range) {
    return [];
  }
  const low = Math.round(freqToMidi(range.lowHz));
  const high = Math.round(freqToMidi(range.highHz));
  const span = high - low;
  if (span <= 0) {
    return [];
  }
  const cut = (fraction: number): number => Math.ceil(low + fraction * span);

  const bounds = cuts.map(({ id, from, to }, i) => ({
    id,
    lowMidi: cut(from),
    highMidi: i === cuts.length - 1 ? high : cut(to) - 1,
  }));

  const midis = samples.map((s) => freqToMidi(s.f0Hz));
  // Which register each reading falls in, once. The bands are contiguous and do not
  // overlap, so a reading has exactly one — asking each register about every reading
  // instead walks the take five times to learn the same thing.
  const at = midis.map((m) =>
    bounds.findIndex((b) => m >= b.lowMidi - 0.5 && m < b.highMidi + 0.5),
  );

  // Steps between neighbouring readings that sit in the SAME register and were close
  // enough in time to be the same phrase.
  const steps: number[][] = bounds.map(() => []);
  for (let j = 1; j < samples.length; j++) {
    const dtMs = samples[j].tMs - samples[j - 1].tMs;
    if (at[j] < 0 || at[j] !== at[j - 1] || dtMs <= 0 || dtMs > MAX_STEP_MS) {
      continue;
    }
    steps[at[j]].push((midis[j] - midis[j - 1]) * 100);
  }

  return bounds.map((bound, i) =>
    steps[i].length < MIN_STEPS
      ? bound
      : {
          ...bound,
          score: Math.round(
            clamp(100 - (100 * std(steps[i])) / ZERO_SPREAD_CENTS),
          ),
        },
  );
};
